import { useRef, useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { DialogFooter } from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { som } from 'shared/lib/money'

type CloseInput = RouterInputs['shifts']['Close']
type CloseResult = RouterOutputs['shifts']['Close']
type ClosedShift = Extract<CloseResult, { outcome: 'closed' }>['shift']

/**
 * Closing one's own Shift (ADR-0035) — the body of the «Закрыть смену» dialog: the
 * operator enters the cash actually in the drawer; a difference from the Cashbox is
 * recorded against them and needs a comment, which the server asks for
 * (`comment_required`) rather than this form guessing. Nothing here writes into the
 * Cashbox.
 *
 * `onBehalf` is the admin closing the Shift of an operator who left without
 * closing it (ticket 14): the same count, but the comment is always required and
 * the difference is the Shift's operator's, not the admin's.
 *
 * Enter moves on like Tab (spec §26): from the count to the comment, from the
 * comment to «Закрыть смену», and a second Enter there confirms.
 *
 * `close` is injected so the behaviour is tested without a backend.
 */
export function ShiftCloseForm({
	close,
	onClosed,
	onCancel,
	onBehalf = false,
}: {
	close: (input: CloseInput) => Promise<CloseResult>
	onClosed?: (shift: ClosedShift) => void
	onCancel?: () => void
	onBehalf?: boolean
}) {
	const { t } = useTranslate()
	const [counted, setCounted] = useState('')
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const commentRef = useRef<HTMLInputElement>(null)
	const submitRef = useRef<HTMLButtonElement>(null)

	const submit = async () => {
		if (counted === '' || busy) return
		setBusy(true)
		setMessage(null)
		try {
			const result = await close({
				countedCash: Math.max(0, Number(counted) || 0),
				comment: comment.trim() || undefined,
			})
			switch (result.outcome) {
				case 'closed':
					onClosed?.(result.shift)
					break
				case 'comment_required':
					setMessage(
						onBehalf
							? t('shift.commentRequiredOnBehalf')
							: t(result.difference < 0 ? 'shift.commentShortage' : 'shift.commentSurplus', {
									amount: som(t, Math.abs(result.difference)),
								}),
					)
					commentRef.current?.focus()
					break
				case 'not_your_shift':
					setMessage(t('shift.notYourShift'))
					break
				case 'no_open_shift':
					setMessage(t('shift.noOpenShift'))
					break
			}
		} catch {
			setMessage(t('shift.closeFailed'))
		} finally {
			setBusy(false)
		}
	}

	return (
		<div
			className='flex flex-col gap-4'
			data-testid={onBehalf ? 'shift-close-on-behalf-form' : 'shift-close-form'}
		>
			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='shift-counted'>{t('shift.countedSum')}</Label>
				<Input
					id='shift-counted'
					data-testid='shift-counted'
					type='number'
					min={0}
					inputMode='numeric'
					autoComplete='off'
					autoFocus
					value={counted}
					onChange={(e) => setCounted(e.target.value)}
					onKeyDown={(e) => {
						if (e.key !== 'Enter') return
						e.preventDefault()
						commentRef.current?.focus()
					}}
				/>
			</div>
			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='shift-comment'>{t('shift.comment')}</Label>
				<Input
					id='shift-comment'
					data-testid='shift-comment'
					ref={commentRef}
					autoComplete='off'
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder={t(onBehalf ? 'shift.commentPlaceholderOnBehalf' : 'shift.commentPlaceholder')}
					onKeyDown={(e) => {
						if (e.key !== 'Enter') return
						e.preventDefault()
						submitRef.current?.focus()
					}}
				/>
			</div>
			{message !== null && (
				<p role='alert' className='text-sm text-destructive' data-testid='shift-close-message'>
					{message}
				</p>
			)}
			<DialogFooter>
				{onCancel && (
					<Button type='button' variant='outline' disabled={busy} onClick={onCancel}>
						{t('shift.closeCancel')}
					</Button>
				)}
				<Button
					ref={submitRef}
					type='button'
					data-testid='shift-close-submit'
					disabled={busy || counted === ''}
					onClick={submit}
				>
					{t(onBehalf ? 'shift.closeOnBehalfTitle' : 'shift.close')}
				</Button>
			</DialogFooter>
		</div>
	)
}
